import { useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';

export interface ConfettiBurstOptions {
  x?: number;
  y?: number;
  particleCount?: number;
  spread?: number;
  colors?: string[];
}

/**
 * Custom hook for triggering confetti effects
 * Provides various celebration animations for wins, records, and completions
 */
export function useConfetti() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  /**
   * Fire confetti burst from both sides (for match wins)
   */
  const fireWinConfetti = useCallback(() => {
    const duration = 1500;
    const animationEnd = Date.now() + duration;
    const colors = ['#9EFF00', '#00e68a', '#119DA4', '#FFFFFF'];

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: colors,
      });

      if (Date.now() < animationEnd) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, []);

  /**
   * Golden rain confetti (for new records)
   */
  const fireRecordConfetti = useCallback(() => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const colors = ['#FFD700', '#FFA500', '#FF8C00', '#FFFF00'];

    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 90,
        spread: 120,
        origin: { x: Math.random(), y: -0.1 },
        colors: colors,
        gravity: 0.8,
        scalar: 1.2,
        shapes: ['circle', 'square'],
      });

      if (Date.now() < animationEnd) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, []);

  /**
   * Full celebration (for tournament completion)
   */
  const fireTournamentCompleteConfetti = useCallback(() => {
    const duration = 4000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const frame = () => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) return;

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#9EFF00', '#00e68a', '#119DA4', '#FFD700'],
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#9EFF00', '#00e68a', '#119DA4', '#FFD700'],
      });

      requestAnimationFrame(frame);
    };

    frame();
  }, []);

  /**
   * Simple burst confetti (for quick celebrations)
   */
  const fireBurst = useCallback((options: ConfettiBurstOptions = {}) => {
    const {
      x = 0.5,
      y = 0.5,
      particleCount = 50,
      spread = 70,
      colors = ['#9EFF00', '#00e68a', '#119DA4'],
    } = options;

    confetti({
      particleCount,
      spread,
      origin: { x, y },
      colors,
      gravity: 1,
      scalar: 1,
    });
  }, []);

  /**
   * Emoji confetti (custom shapes)
   */
  const fireEmojiConfetti = useCallback((emoji: string = '🏓') => {
    const scalar = 2;
    const pongBall = confetti.shapeFromText({ text: emoji, scalar });

    const defaults = {
      spread: 360,
      ticks: 60,
      gravity: 0.5,
      decay: 0.96,
      startVelocity: 20,
      shapes: [pongBall],
      scalar,
    };

    const shoot = () => {
      confetti({
        ...defaults,
        particleCount: 30,
      });

      confetti({
        ...defaults,
        particleCount: 5,
        flat: true,
      });
    };

    shoot();
    setTimeout(shoot, 100);
    setTimeout(shoot, 200);
  }, []);

  /**
   * Side cannons (for dramatic effect)
   */
  const fireSideCannons = useCallback(() => {
    const colors = ['#9EFF00', '#00e68a', '#119DA4', '#FF6B00'];

    // Left cannon
    confetti({
      particleCount: 80,
      angle: 60,
      spread: 50,
      origin: { x: 0, y: 0.8 },
      colors,
      startVelocity: 45,
    });

    // Right cannon
    confetti({
      particleCount: 80,
      angle: 120,
      spread: 50,
      origin: { x: 1, y: 0.8 },
      colors,
      startVelocity: 45,
    });
  }, []);

  /**
   * Reset/clear all confetti
   */
  const clearConfetti = useCallback(() => {
    confetti.reset();
  }, []);

  return {
    fireWinConfetti,
    fireRecordConfetti,
    fireTournamentCompleteConfetti,
    fireBurst,
    fireEmojiConfetti,
    fireSideCannons,
    clearConfetti,
    canvasRef,
  };
}

export default useConfetti;
